import React from 'react';
import { Control, Controller, RegisterOptions } from 'react-hook-form';
import { DateInput, DateInputProps } from '@nextui-org/date-input';
import { PrimeIcons } from 'primereact/api';
import { classNames } from 'primereact/utils';
import { CalendarDateTime } from '@internationalized/date';

type RHFDateTimeInputProps = {
    control: Control<any>;
    name: string;
    rules?: RegisterOptions;
    inputProps?: Partial<DateInputProps>;
};

export const RHFDateTimeInput = ({ control, name, rules, inputProps }: RHFDateTimeInputProps) => {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => (
                <DateInput
                    id={field.name}
                    {...inputProps}
                    value={field.value}
                    onChange={field.onChange}
                    isDisabled={field.disabled}
                    isInvalid={fieldState.invalid}
                    className={classNames('p-component p-inputtext datetime-input', {
                        'p-invalid': fieldState.invalid,
                        'p-disabled': field.disabled,
                    })}
                    granularity='second'
                    hourCycle={24}
                    endContent={
                        field.value && (
                            <i
                                className={`${PrimeIcons.TIMES} datetime-input__clear`}
                                onClick={() => field.onChange(null)}
                            />
                        )
                    }
                    onKeyUp={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        if (field.value) return;

                        const container = (e.target as HTMLElement).parentElement;
                        if (!container) return;

                        const yearEl = container.querySelector('[data-type="year"]');
                        const monthEl = container.querySelector('[data-type="month"]');
                        const dayEl = container.querySelector('[data-type="day"]');
                        const hourEl = container.querySelector('[data-type="hour"]');
                        const minuteEl = container.querySelector('[data-type="minute"]');
                        const secondEl = container.querySelector('[data-type="second"]');

                        if (
                            !yearEl ||
                            !monthEl ||
                            !dayEl ||
                            !hourEl ||
                            !minuteEl ||
                            !secondEl
                        ) {
                            return;
                        }

                        if (
                            yearEl.ariaValueText === 'Empty' &&
                            monthEl.ariaValueText === 'Empty' &&
                            dayEl.ariaValueText === 'Empty' &&
                            hourEl.ariaValueText === 'Empty' &&
                            minuteEl.ariaValueText === 'Empty' &&
                            secondEl.ariaValueText === 'Empty'
                        ) {
                            return;
                        }

                        const year = parseInt(yearEl.ariaValueNow ?? '');
                        const month = parseInt(monthEl.ariaValueNow ?? '');
                        const day = parseInt(dayEl.ariaValueNow ?? '');
                        const hours = parseInt(hourEl.ariaValueNow ?? '');
                        const minutes = parseInt(minuteEl.ariaValueNow ?? '');
                        const seconds = parseInt(secondEl.ariaValueNow ?? '');

                        if ([year, month, day, hours, minutes, seconds].some(isNaN)) {
                            return;
                        }

                        field.onChange(
                            new CalendarDateTime(year, month, day, hours, minutes, seconds),
                        );
                    }}
                />
            )}
        />
    );
};
